import { FC, useRef, useState } from 'react'
import useOutsideClick from 'src/hooks/useOutsideClick'
import styles from './commentsAnalysis.module.scss'

export interface FilterOption {
  label: string
  // undefined is the "All …" option.
  value?: string
}

interface FilterDropdownProps {
  onChange: (value?: string) => void
  options: FilterOption[]
  value?: string
}

/** "All platforms ⌄": a pill that opens a single-choice menu. */
export const FilterDropdown: FC<FilterDropdownProps> = ({
  onChange,
  options,
  value,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })
  const current = options.find((option) => option.value === value) ?? options[0]

  return (
    <div className={styles.filter} ref={ref}>
      <button
        aria-expanded={open}
        className={`${styles.filterButton} ${
          value !== undefined ? styles.filterButtonActive : ''
        }`}
        onClick={() => setOpen((isOpen) => !isOpen)}
        title={current?.label}
        type="button"
      >
        <span className={styles.filterLabel}>{current?.label}</span>
        <svg aria-hidden="true" fill="none" viewBox="0 0 12 12">
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.4"
          />
        </svg>
      </button>
      {open && (
        <div className={styles.filterMenu} role="listbox">
          {options.map((option) => (
            <button
              aria-selected={option.value === value}
              className={`${styles.filterOption} ${
                option.value === value ? styles.filterOptionActive : ''
              }`}
              key={option.value ?? '__all__'}
              onClick={() => {
                onChange(option.value)
                setOpen(false)
              }}
              role="option"
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
